import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, X } from "lucide-react";
import api from "@/lib/api";

const SLUGS = ["landing", "about", "contact"];

export default function AdminPages() {
  const [slug, setSlug] = useState("landing");
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pages/${slug}`);
      setPage(data);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [slug]);

  const setField = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setSection = (i, k, v) => {
    const next = [...(page.sections || [])];
    next[i] = { ...next[i], [k]: v };
    setPage({ ...page, sections: next });
  };
  const addSection = () => setPage({ ...page, sections: [...(page.sections || []), { heading: "", body: "", image_url: "", video_url: "" }] });
  const removeSection = (i) => setPage({ ...page, sections: page.sections.filter((_, ix) => ix !== i) });

  const setArr = (key, i, k, v) => { const next = [...(page[key] || [])]; next[i] = { ...next[i], [k]: v }; setPage({ ...page, [key]: next }); };
  const addArr = (key, obj) => setPage({ ...page, [key]: [...(page[key] || []), obj] });
  const removeArr = (key, i) => setPage({ ...page, [key]: (page[key] || []).filter((_, ix) => ix !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/pages/${slug}`, {
        title: page.title,
        hero_kicker: page.hero_kicker || "",
        hero_title: page.hero_title,
        hero_subtitle: page.hero_subtitle || "",
        hero_image: page.hero_image || null,
        hero_video_url: page.hero_video_url || null,
        sections: page.sections || [],
        contact_email: page.contact_email || null,
        contact_telegram: page.contact_telegram || null,
        contact_whatsapp: page.contact_whatsapp || null,
        contact_location: page.contact_location || null,
        features: page.features || [],
        faqs: page.faqs || [],
        cta_title: page.cta_title || "",
        cta_body: page.cta_body || "",
        footer_tagline: page.footer_tagline || "",
        stats: page.stats || [],
        steps: page.steps || [],
        quote: page.quote || "",
        quote_attribution: page.quote_attribution || "",
      });
      toast.success("Page saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-pages">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Content</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Pages</h1>
          <p className="mt-2 text-sm text-emerald-100/60">Edit About and Contact pages. Content is published live.</p>
        </div>
        <div className="flex gap-2">
          {SLUGS.map((s) => (
            <button key={s} onClick={() => setSlug(s)}
                    data-testid={`admin-pages-tab-${s}`}
                    className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                      slug === s ? "bg-emerald-500/15 text-lime-200" : "text-emerald-100/60 hover:text-white"
                    }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading || !page ? (
        <div className="card-edge p-8 text-emerald-100/50">Loading…</div>
      ) : (
        <form onSubmit={save} className="card-moss grid gap-5 p-6 lg:p-8">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Title (browser tab)" v={page.title} on={(v) => setField("title", v)} />
            <Field label="Hero kicker" v={page.hero_kicker} on={(v) => setField("hero_kicker", v)} />
          </div>
          <Field label="Hero title" v={page.hero_title} on={(v) => setField("hero_title", v)} required />
          <Field label="Hero subtitle" v={page.hero_subtitle} on={(v) => setField("hero_subtitle", v)} textarea />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Hero image URL" v={page.hero_image} on={(v) => setField("hero_image", v)} />
            <Field label="Hero video URL (YouTube/Vimeo/MP4 — shows only if set)" v={page.hero_video_url} on={(v) => setField("hero_video_url", v)} />
          </div>

          {slug === "contact" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Contact email" v={page.contact_email} on={(v) => setField("contact_email", v)} />
              <Field label="Telegram URL" v={page.contact_telegram} on={(v) => setField("contact_telegram", v)} />
              <Field label="WhatsApp URL" v={page.contact_whatsapp} on={(v) => setField("contact_whatsapp", v)} />
              <Field label="Location" v={page.contact_location} on={(v) => setField("contact_location", v)} />
            </div>
          )}

          {slug === "landing" && (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="CTA heading (bottom of home)" v={page.cta_title} on={(v) => setField("cta_title", v)} />
                <Field label="Footer tagline (global)" v={page.footer_tagline} on={(v) => setField("footer_tagline", v)} />
              </div>
              <Field label="CTA body" v={page.cta_body} on={(v) => setField("cta_body", v)} textarea />

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="overline">Feature columns (3 recommended)</p>
                  <button type="button" onClick={() => addArr("features", { title: "", body: "" })} className="btn-edge-ghost text-xs" data-testid="add-feature"><Plus size={12} /> Add feature</button>
                </div>
                <div className="grid gap-4">
                  {(page.features || []).map((f, i) => (
                    <div key={i} className="rounded-xl border border-emerald-500/15 bg-[#0d2416]/50 p-4" data-testid={`feature-${i}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">Feature {i + 1}</p>
                        <button type="button" onClick={() => removeArr("features", i)} className="text-emerald-100/50 hover:text-red-300"><X size={14} /></button>
                      </div>
                      <div className="grid gap-3">
                        <Field label="Title" v={f.title} on={(v) => setArr("features", i, "title", v)} />
                        <Field label="Body" v={f.body} on={(v) => setArr("features", i, "body", v)} textarea />
                      </div>
                    </div>
                  ))}
                  {(page.features || []).length === 0 && <p className="text-xs text-emerald-100/40">Empty = the 3 built-in defaults are shown on the homepage.</p>}
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="overline">FAQ</p>
                  <button type="button" onClick={() => addArr("faqs", { q: "", a: "" })} className="btn-edge-ghost text-xs" data-testid="add-faq"><Plus size={12} /> Add question</button>
                </div>
                <div className="grid gap-4">
                  {(page.faqs || []).map((f, i) => (
                    <div key={i} className="rounded-xl border border-emerald-500/15 bg-[#0d2416]/50 p-4" data-testid={`faq-edit-${i}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">Q{i + 1}</p>
                        <button type="button" onClick={() => removeArr("faqs", i)} className="text-emerald-100/50 hover:text-red-300"><X size={14} /></button>
                      </div>
                      <div className="grid gap-3">
                        <Field label="Question" v={f.q} on={(v) => setArr("faqs", i, "q", v)} />
                        <Field label="Answer" v={f.a} on={(v) => setArr("faqs", i, "a", v)} textarea />
                      </div>
                    </div>
                  ))}
                  {(page.faqs || []).length === 0 && <p className="text-xs text-emerald-100/40">Empty = the built-in FAQ is shown on the homepage.</p>}
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="overline">"By the numbers" stats</p>
                  <button type="button" onClick={() => addArr("stats", { value: "", label: "" })} className="btn-edge-ghost text-xs" data-testid="add-stat"><Plus size={12} /> Add stat</button>
                </div>
                <div className="grid gap-3">
                  {(page.stats || []).map((s, i) => (
                    <div key={i} className="grid gap-2 rounded-xl border border-emerald-500/15 bg-[#0d2416]/50 p-4 md:grid-cols-[1fr_2fr_auto]" data-testid={`stat-${i}`}>
                      <Field label="Value (e.g. 6+ yrs)" v={s.value} on={(v) => setArr("stats", i, "value", v)} />
                      <Field label="Label" v={s.label} on={(v) => setArr("stats", i, "label", v)} />
                      <button type="button" onClick={() => removeArr("stats", i)} className="self-end pb-2 text-emerald-100/50 hover:text-red-300"><X size={14} /></button>
                    </div>
                  ))}
                  {(page.stats || []).length === 0 && <p className="text-xs text-emerald-100/40">Empty = the built-in stats row is shown.</p>}
                </div>
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <p className="overline">"How it works" steps</p>
                  <button type="button" onClick={() => addArr("steps", { title: "", body: "" })} className="btn-edge-ghost text-xs" data-testid="add-step"><Plus size={12} /> Add step</button>
                </div>
                <div className="grid gap-4">
                  {(page.steps || []).map((s, i) => (
                    <div key={i} className="rounded-xl border border-emerald-500/15 bg-[#0d2416]/50 p-4" data-testid={`step-${i}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">Step {i + 1}</p>
                        <button type="button" onClick={() => removeArr("steps", i)} className="text-emerald-100/50 hover:text-red-300"><X size={14} /></button>
                      </div>
                      <div className="grid gap-3">
                        <Field label="Title" v={s.title} on={(v) => setArr("steps", i, "title", v)} />
                        <Field label="Body" v={s.body} on={(v) => setArr("steps", i, "body", v)} textarea />
                      </div>
                    </div>
                  ))}
                  {(page.steps || []).length === 0 && <p className="text-xs text-emerald-100/40">Empty = the built-in 3 steps are shown.</p>}
                </div>
              </div>

              <Field label="Philosophy quote" v={page.quote} on={(v) => setField("quote", v)} textarea />
              <Field label="Quote attribution (e.g. — HK Forexedge desk, since 2019)" v={page.quote_attribution} on={(v) => setField("quote_attribution", v)} />
            </>
          )}

          <div>
            <div className="mb-3 flex items-center justify-between">
              <p className="overline">Sections</p>
              <button type="button" onClick={addSection} className="btn-edge-ghost text-xs">
                <Plus size={12} /> Add section
              </button>
            </div>
            <div className="grid gap-4">
              {(page.sections || []).map((s, i) => (
                <div key={i} className="rounded-xl border border-emerald-500/15 bg-[#0d2416]/50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">Section {i + 1}</p>
                    <button type="button" onClick={() => removeSection(i)} className="text-emerald-100/50 hover:text-red-300">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="grid gap-3">
                    <Field label="Heading" v={s.heading} on={(v) => setSection(i, "heading", v)} />
                    <Field label="Body" v={s.body} on={(v) => setSection(i, "body", v)} textarea />
                    <Field label="Image URL (optional)" v={s.image_url} on={(v) => setSection(i, "image_url", v)} />
                    <Field label="Video URL (optional — shows only if set)" v={s.video_url} on={(v) => setSection(i, "video_url", v)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button disabled={saving} data-testid={`page-save-${slug}`} className="btn-edge justify-center">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save page
          </button>
        </form>
      )}
    </div>
  );
}

function Field({ label, v, on, textarea, required }) {
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      {textarea ? (
        <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={4}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
      ) : (
        <input value={v ?? ""} onChange={(e) => on(e.target.value)} required={required}
          className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
      )}
    </div>
  );
}
